import React, { useState } from 'react';
import { Popover, Slider } from '@mui/material';
import { Settings2, X, ChevronUp, ChevronDown } from 'lucide-react';

export default function DefaultsPopover({ defaults, onChange }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);
  const setQty = (v) => onChange({ ...defaults, qty: Math.max(1, Number(v) || 1) });
  const setDisc = (v) => onChange({ ...defaults, discountPercent: Math.min(100, Math.max(0, Number(v) || 0)) });
  const hasCustom = defaults.qty !== 1 || defaults.discountPercent !== 0;

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <button
        onClick={handleClick}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold rounded-lg border transition-all ${
          open
            ? 'bg-[#1e293b] text-white border-[#1e293b]'
            : hasCustom
              ? 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100'
              : 'bg-white text-[#1e293b] border-slate-200 hover:bg-slate-50'
        }`}
      >
        <Settings2 size={12} />
        Defaults
        {hasCustom && !open && (
          <span className="text-[9px] font-extrabold bg-amber-500 text-white rounded-full px-1.5 py-0.5 leading-none">
            {[defaults.qty !== 1 && `×${defaults.qty}`, defaults.discountPercent !== 0 && `${defaults.discountPercent}%`].filter(Boolean).join(' ')}
          </span>
        )}
      </button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          style: { 
            marginTop: '6px', 
            borderRadius: '12px', 
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
            border: '1px solid #e2e8f0'
          },
        }}
      >
        <div style={{ minWidth: 260, fontFamily: "'DM Sans', sans-serif" }}>
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center gap-2">
              <Settings2 size={13} className="text-[#1e293b]" />
              <p className="text-[11px] font-bold text-[#1e293b]">Default Attributes</p>
            </div>
            <button onClick={handleClose} className="text-slate-400 hover:text-[#1e293b] transition-colors">
              <X size={13} />
            </button>
          </div>

          <div className="px-4 py-4 space-y-5">
            {/* Default Qty */}
            <div>
              <label className="block text-[10px] font-bold text-[#1e293b] opacity-60 uppercase tracking-wider mb-2">Default Quantity</label>
              <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden bg-white">
                <button onClick={() => setQty(defaults.qty - 1)} className="w-9 h-9 flex items-center justify-center text-[#1e293b] hover:bg-slate-50 border-r border-slate-200"><ChevronDown size={13} /></button>
                <input 
                  type="number"
                  value={defaults.qty}
                  onChange={(e) => setQty(e.target.value)}
                  className="flex-1 text-center text-sm font-bold text-[#1e293b] border-none focus:ring-0 outline-none"
                />
                <button onClick={() => setQty(defaults.qty + 1)} className="w-9 h-9 flex items-center justify-center text-[#1e293b] hover:bg-slate-50 border-l border-slate-200"><ChevronUp size={13} /></button>
              </div>
            </div>

            {/* Default Discount Slider */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-[10px] font-bold text-[#1e293b] opacity-60 uppercase tracking-wider">Default Discount</label>
                <div className="flex items-center gap-1 bg-slate-100 px-1.5 py-0.5 rounded transition-all">
                   <input 
                    type="number"
                    value={defaults.discountPercent}
                    onChange={(e) => setDisc(e.target.value)}
                    className="w-12 text-xs font-black text-[#1e293b] bg-transparent border-none p-0 focus:ring-0 outline-none text-right"
                  />
                  <span className="text-[10px] font-bold text-[#1e293b] opacity-50">%</span>
                </div>
              </div>
              <div className="px-2">
                <Slider
                  value={defaults.discountPercent}
                  onChange={(e, v) => setDisc(v)}
                  step={1}
                  min={0}
                  max={100}
                  size="small"
                  sx={{
                    color: '#1e293b',
                    height: 6,
                    '& .MuiSlider-track': { border: 'none' },
                    '& .MuiSlider-thumb': {
                      width: 14,
                      height: 14,
                      backgroundColor: '#fff',
                      border: '2px solid currentColor',
                      '&:hover, &.Mui-focusVisible, &.Mui-active': { boxShadow: '0 0 0 8px rgba(30, 41, 59, 0.1)' },
                    },
                  }}
                />
              </div>
              <div className="flex justify-between text-[9px] font-bold text-slate-700 px-1">
                <span>0%</span>
                <span>100%</span>
              </div>
            </div>

            {hasCustom && (
              <button onClick={() => { onChange({ qty: 1, discountPercent: 0 }); handleClose(); }}
                className="w-full py-1.5 text-[11px] font-semibold text-slate-700 hover:text-red-600 hover:bg-red-50 rounded-lg border border-slate-200 transition-all">
                Reset to defaults
              </button>
            )}
          </div>
        </div>
      </Popover>
    </div>
  );
}